import type { CSSProperties } from 'react';
import { interpolate } from 'remotion';

import {
  calculateSsulImageEntranceStyle as imageEntranceStyle,
  calculateSsulOverlayStyle,
} from '@/shared/lib/remotion/ssul-overlay-effects';

interface TemplateEffectSeed {
  kind?: unknown;
  sceneStartFrame?: unknown;
  sceneEndFrame?: unknown;
  enterFx?: unknown;
  motionFx?: unknown;
  overlayFx?: unknown;
}

interface TemplateEffect {
  sceneStartFrame: number;
  sceneEndFrame: number;
  enterFx: string;
  motionFx: string;
  overlayFx: string | null;
}

export interface TemplateEffectHost {
  from: number;
  templateEffect?: unknown;
  ssulTemplateEffect?: unknown;
}

interface CalculatedTemplateEffect {
  outerStyle: CSSProperties;
  innerStyle: CSSProperties;
  overlayOpacity: number;
  overlayStyle: CSSProperties | null;
}

const readNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const readRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readTemplateEffect = (
  item: TemplateEffectHost,
): TemplateEffect | null => {
  const seed = readRecord(
    item.templateEffect ?? item.ssulTemplateEffect,
  ) as TemplateEffectSeed | null;
  if (
    !seed ||
    (seed.kind !== 'template-image-card' &&
      seed.kind !== 'ssul-template-image-card')
  ) {
    return null;
  }
  const sceneStartFrame = readNumber(seed.sceneStartFrame);
  const sceneEndFrame = readNumber(seed.sceneEndFrame);
  if (sceneStartFrame == null || sceneEndFrame == null) return null;
  if (sceneEndFrame <= sceneStartFrame) return null;
  return {
    sceneStartFrame,
    sceneEndFrame,
    enterFx: readString(seed.enterFx) ?? 'fade-up',
    motionFx: readString(seed.motionFx) ?? 'close-up',
    overlayFx: readString(seed.overlayFx),
  };
};

const clampInterpolate = (
  value: number,
  input: number[],
  output: number[],
): number =>
  interpolate(value, input, output, {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

const imageMotionStyle = (
  fx: string,
  elapsedFrames: number,
  durationFrames: number,
): CSSProperties => {
  const progress = clampInterpolate(
    elapsedFrames,
    [0, Math.max(1, durationFrames)],
    [0, 1],
  );
  if (fx === 'close-up') {
    return { transform: `translate(0px, 0px) scale(${1 + progress * 0.08})` };
  }
  if (fx === 'pan-left') {
    return {
      transform: `translate(${18 - progress * 36}px, 0px) scale(1.06)`,
    };
  }
  if (fx === 'pan-right') {
    return {
      transform: `translate(${-18 + progress * 36}px, 0px) scale(1.06)`,
    };
  }
  if (fx === 'drift-up') {
    return {
      transform: `translate(0px, ${18 - progress * 36}px) scale(1.045)`,
    };
  }
  if (fx === 'deep-close-up') {
    return {
      transform: `translate(0px, 0px) scale(${1.02 + progress * 0.18})`,
    };
  }
  if (fx === 'push-pan-left') {
    return {
      transform: `translate(${42 - progress * 88}px, 0px) scale(1.12)`,
    };
  }
  if (fx === 'push-pan-right') {
    return {
      transform: `translate(${-42 + progress * 88}px, 0px) scale(1.12)`,
    };
  }
  if (fx === 'tilt-drift') {
    const y = 24 - progress * 52;
    const rotate = -1.2 + progress * 2.4;
    return {
      transform: `translate(0px, ${y}px) rotate(${rotate}deg) scale(1.1)`,
    };
  }
  return { transform: 'translate(0px, 0px) scale(1)' };
};

export const calculateTemplateEffect = ({
  item,
  localFrame,
  fps,
}: {
  item: TemplateEffectHost;
  localFrame: number;
  fps: number;
}): CalculatedTemplateEffect | null => {
  const effect = readTemplateEffect(item);
  if (!effect) return null;
  const globalFrame = item.from + localFrame;
  const elapsedFrames = globalFrame - effect.sceneStartFrame;
  const durationFrames = Math.max(
    1,
    effect.sceneEndFrame - effect.sceneStartFrame,
  );
  const entrance = imageEntranceStyle(effect.enterFx, elapsedFrames, fps);
  return {
    outerStyle: entrance.style,
    innerStyle: imageMotionStyle(
      effect.motionFx,
      elapsedFrames,
      durationFrames,
    ),
    overlayOpacity: entrance.overlayOpacity,
    overlayStyle: calculateSsulOverlayStyle(
      effect.overlayFx,
      elapsedFrames,
      fps,
      globalFrame,
    ),
  };
};

export const mergeTemplateStyle = (
  baseStyle: CSSProperties,
  effectStyle: CSSProperties,
): CSSProperties => {
  const merged = { ...baseStyle, ...effectStyle };
  if (
    typeof baseStyle.opacity === 'number' &&
    typeof effectStyle.opacity === 'number'
  ) {
    merged.opacity = baseStyle.opacity * effectStyle.opacity;
  }
  if (baseStyle.transform && effectStyle.transform) {
    merged.transform = `${baseStyle.transform} ${effectStyle.transform}`;
  }
  if (baseStyle.filter && effectStyle.filter) {
    merged.filter = `${baseStyle.filter} ${effectStyle.filter}`;
  }
  return merged;
};
